import api from "../../config/api";
import { handleApiError } from "@/utils/ErrorHandler";
import { createPaginationModule, defaultPagination } from "@/store/common/pagination";

const recurrencesPag = createPaginationModule('RECURRENCES');

const state = {
    recurrences: [],
    recurrenceDetails: {},
    pagination: defaultPagination(),
    loading: false,
    error: null,
};

const mutations = {
    ...recurrencesPag.mutations,
    SET_RECURRENCES(state, recurrences) {
        state.recurrences = recurrences;
    },
    SET_RECURRENCE_DETAILS(state, recurrences) {
        state.recurrenceDetails = recurrences;
    },
    SET_LOADING(state, loading) {
        state.loading = loading;
    },
    SET_ERROR(state, error) {
        state.error = error;
    },
};

const actions = {
    /**
     * Fetch recurrences for an activity with pagination
     */
    async fetchRecurrences({ commit }, { activityId, pageNumber = 1, pageSize = 10 }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "getrecurrences",
                activityid: activityId,
                page_size: pageSize,
                page_number: pageNumber,
            });

            const recurrences = response.data?.data?.recurrences || [];
            const pagination = response.data?.data.pagination || defaultPagination();

            commit("SET_RECURRENCES", recurrences);
            commit(recurrencesPag.SET_PAGINATION, pagination);

            return { recurrences, pagination };
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch recurrences");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * Add a new recurrence
     */
    async addRecurrence({ commit }, payload) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "addrecurrence",
                ...payload,
            });

            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to add recurrence");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * Edit an existing recurrence
     */
    async editRecurrence({ commit }, payload) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "editrecurrence",
                ...payload,
            });

            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to edit recurrence");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },

    /**
     * Delete a recurrence
     */
    async deleteRecurrence({ commit }, { locationId, activityId, recurrenceId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "deleterecurrence",
                locationid: locationId,
                activityid: activityId,
                recurrenceid: recurrenceId,
            });

            return response.data;
        } catch (error) {
            const message = handleApiError(error, "Failed to delete recurrence");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
    async getRecurrenceDetail({ commit }, { activityId, recurrenceId }) {
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);

        try {
            const response = await api.post("/manageactivityajax/", {
                cmd: "getRecurrenceDetail",
                activityid: activityId,
                recurrenceid: recurrenceId,
            });

            commit("SET_RECURRENCE_DETAILS", response.data?.recurrence);
            return response.data?.recurrence || null;
        } catch (error) {
            const message = handleApiError(error, "Failed to fetch recurrence details");
            commit("SET_ERROR", message);
            throw new Error(message);
        } finally {
            commit("SET_LOADING", false);
        }
    },
};

const getters = {
    recurrences: (state) => state.recurrences,
    recurrenceDetails: (state) => state.recurrenceDetails,
    recurrencesPagination: (state) => state.pagination,
    recurrencesLoading: (state) => state.loading,
    recurrencesError: (state) => state.error,
};

export default {
    state,
    mutations,
    actions,
    getters,
};
